import React, { useState, useEffect } from "react";
import { NavLink } from "react-router-dom";
import axios from "axios";
import { toast } from "react-toastify";
import { REACT_APP_API_URL } from "config/api-config";
import { update_user_alerts, delete_telegram } from "services/api-service";
import HRMSSetup from "./HRMSSetup";
import { 
  UserPlus, UserCog, ShieldCheck, MessageCircle, Users, 
  Briefcase, LineChart, ExternalLink, Bell, AlertTriangle, 
  ServerOff, VideoOff, SendHorizontal, Mail, RefreshCw, Activity, Wifi 
} from "lucide-react";
import TelegramSetup from "./TelegramSetup";
import ERPSetup from "./ERPSetup";
import OTAUpdatePanel from "./OTAUpdatePanel"; 

export default function Setting() {
  const [activeTab, setActiveTab] = useState("users");
  const [loading, setLoading] = useState(false);
  const [showTelegramModal, setShowTelegramModal] = useState(false);
  const [showErpModal, setShowErpModal] = useState(false);
  const [showHrmsModal, setShowHrmsModal] = useState(false);
  const [accountDetails, setAccountDetails] = useState({
    name: "Loading...", id: "---", phone: "Loading...", email: "Loading...", role: "Loading...", company: "Loading...",
    alert_unknown_person: false,
    notify_unknown_person: false,
    notify_unknown_start_time: "00:00",
    notify_unknown_end_time: "23:59",
    alert_device_offline: false,
    alert_camera_offline: false,
    enable_people_counting: true,
    telegram_chat_ids: []
  });

  useEffect(() => {
    fetchMyProfile();
  }, []);

  const fetchMyProfile = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem("accessToken");
      const response = await axios.get(`${REACT_APP_API_URL}/users/me`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAccountDetails({
        ...response.data,
        enable_people_counting: response.data.enable_people_counting ?? true,
        notify_unknown_start_time: response.data.notify_unknown_start_time || "00:00",
        notify_unknown_end_time: response.data.notify_unknown_end_time || "23:59"
      });
    } catch (error) {
      console.error("Failed to fetch account details:", error);
      toast.error("Failed to load settings");
    } finally {
      setLoading(false);
    }
  };

  const handleTimeChange = async (field, value) => {
    const updatedDetails = { ...accountDetails, [field]: value };
    setAccountDetails(updatedDetails);
    
    try {
      await update_user_alerts({
        alert_unknown_person: updatedDetails.alert_unknown_person,
        notify_unknown_person: updatedDetails.notify_unknown_person,
        notify_unknown_start_time: updatedDetails.notify_unknown_start_time,
        notify_unknown_end_time: updatedDetails.notify_unknown_end_time,
        alert_device_offline: updatedDetails.alert_device_offline,
        alert_camera_offline: updatedDetails.alert_camera_offline,
        enable_people_counting: updatedDetails.enable_people_counting
      });
      toast.success("Time window updated!");
    } catch (error) {
      toast.error("Failed to save time window.");
    }
  };

  const handleToggleAlert = async (alertType) => {
    let newStatus = !accountDetails[alertType];
    let updatedDetails = { ...accountDetails, [alertType]: newStatus };
    
    if (alertType === 'alert_unknown_person' && !newStatus) {
      updatedDetails.notify_unknown_person = false;
    }
    
    setAccountDetails(updatedDetails);

    try {
      await update_user_alerts({
        alert_unknown_person: updatedDetails.alert_unknown_person,
        notify_unknown_person: updatedDetails.notify_unknown_person,
        notify_unknown_start_time: updatedDetails.notify_unknown_start_time,
        notify_unknown_end_time: updatedDetails.notify_unknown_end_time,
        alert_device_offline: updatedDetails.alert_device_offline,
        alert_camera_offline: updatedDetails.alert_camera_offline,
        enable_people_counting: updatedDetails.enable_people_counting
      });
      if(newStatus) toast.success("Preference Saved!");
      else toast.warn("Preference Disabled");
    } catch (error) {
      toast.error("Failed to save preference. Reverting...");
      fetchMyProfile();
    }
  };

  const integrations = [
    { name: "Telegram Integration", desc: "Receive real-time alerts and daily attendance reports directly on your Telegram app.", action: () => setShowTelegramModal(true), icon: <MessageCircle size={28} color="#0088cc" />, color: "#e6f5ff" },
    { name: "SMS / WhatsApp", desc: "Instant text and WhatsApp notifications for critical events and alerts.", comingSoon: true, icon: <MessageCircle size={28} color="#25D366" />, color: "#e9fced" },
    { name: "Email Integration", desc: "Receive automated daily summaries, attendance reports, and critical alerts directly in your inbox.", comingSoon: true, icon: <Mail size={28} color="#EA4335" />, color: "#fceceb" },
    { name: "HRMS Integration", desc: "Seamlessly sync staff profiles and logs with your Human Resource Management System.", action: () => setShowHrmsModal(true), icon: <Users size={28} color="#4318FF" />, color: "#f0ebff" },
    { name: " ERP Integration", desc: "Connect Visitor Management systems directly to CamSec Ai for automatic roster sync.", action: () => setShowErpModal(true), icon: <Briefcase size={28} color="#FF912C" />, color: "#fff4eb" },
    { name: "MIS Integration", desc: "Automate data exports to your Management Information Systems for advanced reporting.", link: "https://promini.camsecai.com/MIS-Integration", icon: <LineChart size={28} color="#05CD99" />, color: "#e6fbf4" },
  ];

  return (
    <div style={{ backgroundColor: "#F4F7FE", minHeight: "100vh", padding: "30px", fontFamily: "'Inter', sans-serif" }}>
      <div style={{ marginBottom: "30px" }}>
        <h2 style={{ margin: 0, color: "#2B3674", fontSize: "32px", fontWeight: "700" }}>Settings & Setup</h2>
        <p style={{ margin: "5px 0 0 0", color: "#A3AED0", fontSize: "15px" }}>Manage your users, account details, alerts, integrations, and updates.</p>
      </div>

      <div style={{ display: "inline-flex", backgroundColor: "#FFFFFF", padding: "6px", borderRadius: "16px", boxShadow: "0px 4px 20px rgba(0,0,0,0.03)", marginBottom: "30px", flexWrap: "wrap", gap: "5px" }}>
        <TabButton active={activeTab === "users"} onClick={() => setActiveTab("users")}><span style={{display:'flex', alignItems:'center', gap:'6px'}}><Users size={16}/> User Management</span></TabButton>
        <TabButton active={activeTab === "account"} onClick={() => setActiveTab("account")}><span style={{display:'flex', alignItems:'center', gap:'6px'}}><ShieldCheck size={16}/> My Account</span></TabButton>
        <TabButton active={activeTab === "notifications"} onClick={() => setActiveTab("notifications")}><span style={{display:'flex', alignItems:'center', gap:'6px'}}><Bell size={16}/> Features & Alerts</span></TabButton>
        <TabButton active={activeTab === "integrations"} onClick={() => setActiveTab("integrations")}><span style={{display:'flex', alignItems:'center', gap:'6px'}}><ExternalLink size={16}/> Integrations</span></TabButton>
        <TabButton active={activeTab === "updates"} onClick={() => setActiveTab("updates")}><span style={{display:'flex', alignItems:'center', gap:'6px'}}><RefreshCw size={16}/> System Updates</span></TabButton>
        <TabButton active={activeTab === "network"} onClick={() => setActiveTab("network")}><span style={{display:'flex', alignItems:'center', gap:'6px'}}><Wifi size={16}/> Network Setup</span></TabButton>
      </div>

      {activeTab === "users" && (
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(300px, 400px))", gap: "20px" }}>
          <NavLink to="/admin/add_user" style={{ textDecoration: "none" }}><ActionCard title="Add New User" desc="Create a new administrator or staff account." icon={<UserPlus size={32} color="#4318FF" />} bgColor="#f0ebff" /></NavLink>
          <NavLink to="/admin/delete_user" style={{ textDecoration: "none" }}><ActionCard title="Modify / Delete User" desc="Edit permissions or remove existing users from the system." icon={<UserCog size={32} color="#EE5D50" />} bgColor="#ffeeec" /></NavLink>
        </div>
      )}

      {activeTab === "account" && (
        <div style={{ backgroundColor: "#FFFFFF", padding: "30px", borderRadius: "20px", boxShadow: "0px 4px 20px rgba(0,0,0,0.04)", maxWidth: "600px", position: "relative" }}>
          {loading && <span style={{ position: "absolute", top: "20px", right: "20px", color: "#4318FF", fontSize: "13px", fontWeight: "600" }}>Updating...</span>}
          <div style={{ display: "flex", alignItems: "center", gap: "20px", marginBottom: "30px", borderBottom: "1px solid #F4F7FE", paddingBottom: "25px" }}>
            <div style={{ width: "70px", height: "70px", borderRadius: "50%", backgroundColor: "#4318FF", display: "flex", alignItems: "center", justifyContent: "center", color: "white", fontSize: "28px", fontWeight: "bold" }}>{accountDetails.name.charAt(0)}</div>
            <div><h3 style={{ margin: "0", color: "#2B3674", fontSize: "22px", fontWeight: "700" }}>{accountDetails.name}</h3><p style={{ margin: "4px 0 0 0", color: "#A3AED0", fontSize: "14px" }}>{accountDetails.email}</p></div>
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "20px", marginBottom: "25px" }}>
            <DetailItem label="Account ID" value={accountDetails.id} />
            <DetailItem label="Phone Number" value={accountDetails.phone} />
            <DetailItem label="Campus" value={accountDetails.company} />
            <div>
              <p style={{ margin: "0 0 5px 0", color: "#A3AED0", fontSize: "12px", fontWeight: "600" }}>Role</p>
              <div style={{ display: "inline-flex", alignItems: "center", gap: "6px", backgroundColor: "#05CD99", color: "white", padding: "6px 14px", borderRadius: "8px", fontSize: "13px", fontWeight: "700" }}><ShieldCheck size={16} /> {accountDetails.role}</div>
            </div>
          </div>
          <div style={{ backgroundColor: "#F8FAFC", padding: "20px", borderRadius: "12px", border: "1px solid #E2E8F0" }}>
            <p style={{ margin: "0 0 12px 0", color: "#2B3674", fontSize: "14px", fontWeight: "700", display: "flex", alignItems: "center", gap: "6px" }}><MessageCircle size={18} color="#0088cc" /> Connected Telegram Devices</p>
            {accountDetails.telegram_chat_ids && accountDetails.telegram_chat_ids.length > 0 ? (
              <div style={{ display: "flex", flexWrap: "wrap", gap: "10px" }}>
                {accountDetails.telegram_chat_ids.map((device, idx) => (
                  <div key={idx} style={{ backgroundColor: "#e6f5ff", color: "#0088cc", padding: "6px 12px", borderRadius: "8px", fontSize: "13px", fontWeight: "600", display: "flex", alignItems: "center", gap: "8px" }}>
                    {device.name} (ID: {device.id})
                    <button onClick={async () => { try { await delete_telegram(device.id); toast.info(`Device '${device.name}' removed`); fetchMyProfile(); } catch (error) { toast.error("Failed to remove device"); } }} style={{ background: "none", border: "none", color: "#EE5D50", cursor: "pointer", fontWeight: "bold", padding: "0 4px", fontSize: "16px", lineHeight: "1" }} title="Remove Device">✕</button>
                  </div>
                ))}
              </div>
            ) : (<p style={{ margin: "0", color: "#A3AED0", fontSize: "13px", fontWeight: "500" }}>No Telegram devices connected yet.</p>)}
          </div>
        </div>
      )}

      {activeTab === "notifications" && (
        <div style={{ backgroundColor: "#FFFFFF", padding: "30px", borderRadius: "20px", boxShadow: "0px 4px 20px rgba(0,0,0,0.04)", maxWidth: "800px" }}>
          <h3 style={{ margin: "0 0 5px 0", color: "#2B3674", fontSize: "20px", fontWeight: "700" }}>System Features & Preferences</h3>
          <p style={{ margin: "0 0 25px 0", color: "#A3AED0", fontSize: "14px" }}>Manage core modules and notification settings.</p>

          <div style={{ display: "flex", flexDirection: "column", gap: "15px" }}>
            <div style={{ border: "1px solid #F4F7FE", borderRadius: "16px", overflow: "hidden" }}>
              <ToggleCard 
                title="People Counting" 
                desc="Enable real-time tracking of overall Office footfall and non-registered visitors."
                icon={<Activity size={24} color="#05CD99" />}
                bgColor="#e6fbf4"
                checked={accountDetails.enable_people_counting}
                onChange={() => handleToggleAlert('enable_people_counting')}
              />
            </div>

            <div style={{ border: "1px solid #F4F7FE", borderRadius: "16px", overflow: "hidden" }}>
              <ToggleCard 
                title="Unknown / Unauthorized Alerts" 
                desc="Get alerted instantly when a face is detected that is not registered in the system."
                icon={<AlertTriangle size={24} color="#FF912C" />}
                bgColor="#fff4eb"
                checked={accountDetails.alert_unknown_person}
                onChange={() => handleToggleAlert('alert_unknown_person')}
              />
              {accountDetails.alert_unknown_person && (
                <div style={{ backgroundColor: "#F8FAFC", padding: "16px 24px 20px 88px", borderTop: "1px dashed #E2E8F0" }}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: accountDetails.notify_unknown_person ? "15px" : "0" }}>
                    <div>
                      <h5 style={{ margin: "0 0 2px 0", color: "#2B3674", fontSize: "14px", fontWeight: "700", display: 'flex', alignItems: 'center', gap: '6px' }}><SendHorizontal size={14}/> Push Notifications</h5>
                      <p style={{ margin: 0, color: "#A3AED0", fontSize: "12px" }}>Send immediate system popups/desktop notifications for this alert type.</p>
                    </div>
                    <label style={{ display: "flex", alignItems: "center", cursor: "pointer", position: "relative" }}>
                      <input type="checkbox" style={{ opacity: 0, position: "absolute", width: 0, height: 0 }} checked={accountDetails.notify_unknown_person} onChange={() => handleToggleAlert('notify_unknown_person')} />
                      <div style={{ width: "40px", height: "22px", backgroundColor: accountDetails.notify_unknown_person ? "#4318FF" : "#CBD5E1", borderRadius: "20px", position: "relative", transition: "0.3s", display: "flex", alignItems: "center" }}>
                        <div style={{ width: "16px", height: "16px", backgroundColor: "white", borderRadius: "50%", position: "absolute", left: accountDetails.notify_unknown_person ? "21px" : "3px", transition: "0.3s" }}></div>
                      </div>
                    </label>
                  </div>
                  
                  {accountDetails.notify_unknown_person && (
                    <div style={{ display: "flex", alignItems: "center", gap: "15px", backgroundColor: "#fff", padding: "10px 15px", borderRadius: "10px", border: "1px solid #E2E8F0" }}>
                      <span style={{ fontSize: "13px", color: "#2B3674", fontWeight: "600" }}>Active Window:</span>
                      <input 
                        type="time" 
                        value={accountDetails.notify_unknown_start_time}
                        onChange={(e) => handleTimeChange('notify_unknown_start_time', e.target.value)}
                        style={{ padding: "6px", borderRadius: "6px", border: "1px solid #CBD5E1", color: "#2B3674", outline: "none" }}
                      />
                      <span style={{ fontSize: "13px", color: "#A3AED0" }}>to</span>
                      <input 
                        type="time" 
                        value={accountDetails.notify_unknown_end_time}
                        onChange={(e) => handleTimeChange('notify_unknown_end_time', e.target.value)}
                        style={{ padding: "6px", borderRadius: "6px", border: "1px solid #CBD5E1", color: "#2B3674", outline: "none" }}
                      />
                    </div>
                  )}
                </div>
              )}
            </div>

            <div style={{ border: "1px solid #F4F7FE", borderRadius: "16px" }}>
              <ToggleCard 
                title="Device Offline Alert" 
                desc="Receive a notification if the main processing device loses internet connection or power."
                icon={<ServerOff size={24} color="#EE5D50" />}
                bgColor="#ffeeec"
                checked={accountDetails.alert_device_offline}
                onChange={() => handleToggleAlert('alert_device_offline')}
              />
            </div>
            <div style={{ border: "1px solid #F4F7FE", borderRadius: "16px" }}>
              <ToggleCard 
                title="Camera Disconnection Alert" 
                desc="Be notified immediately if an RTSP camera stream drops or a camera goes offline."
                icon={<VideoOff size={24} color="#4318FF" />}
                bgColor="#f0ebff"
                checked={accountDetails.alert_camera_offline}
                onChange={() => handleToggleAlert('alert_camera_offline')}
              />
            </div>
          </div>
        </div>
      )}

      {activeTab === "integrations" && (
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(320px, 1fr))", gap: "24px" }}>
          {integrations.map((integ, index) => (
            <div key={index} style={{ backgroundColor: "#FFFFFF", borderRadius: "20px", padding: "24px", boxShadow: "0px 4px 20px rgba(0,0,0,0.03)", display: "flex", flexDirection: "column", transition: "transform 0.2s ease-in-out" }} onMouseOver={(e) => e.currentTarget.style.transform = "translateY(-5px)"} onMouseOut={(e) => e.currentTarget.style.transform = "translateY(0)"}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: "16px" }}>
                <div style={{ width: "56px", height: "56px", borderRadius: "14px", backgroundColor: integ.color, display: "flex", alignItems: "center", justifyContent: "center" }}>{integ.icon}</div>
                {integ.comingSoon && <span style={{ backgroundColor: "#FFCE20", color: "#2B3674", padding: "4px 12px", borderRadius: "20px", fontSize: "11px", fontWeight: "800" }}>COMING SOON</span>}
              </div>
              <h4 style={{ margin: "0 0 10px 0", color: "#2B3674", fontSize: "18px", fontWeight: "700" }}>{integ.name}</h4>
              <p style={{ margin: "0 0 24px 0", color: "#A3AED0", fontSize: "14px", lineHeight: "1.5", flexGrow: 1 }}>{integ.desc}</p>
              {integ.action ? (
                <button onClick={integ.action} style={{ width: "100%", padding: "12px", backgroundColor: "#4318FF", color: "white", border: "none", borderRadius: "12px", fontWeight: "600", fontSize: "14px", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", gap: "8px", transition: "background 0.2s" }}>Configure Integration <ExternalLink size={16}/></button>
              ) : (
                <a href={integ.comingSoon ? "#" : integ.link} target={integ.comingSoon ? "_self" : "_blank"} rel="noreferrer" style={{ textDecoration: "none" }}>
                  <button disabled={integ.comingSoon} style={{ width: "100%", padding: "12px", backgroundColor: integ.comingSoon ? "#F4F7FE" : "#4318FF", color: integ.comingSoon ? "#A3AED0" : "white", border: "none", borderRadius: "12px", fontWeight: "600", fontSize: "14px", cursor: integ.comingSoon ? "not-allowed" : "pointer", display: "flex", alignItems: "center", justifyContent: "center", gap: "8px", transition: "background 0.2s" }}>
                    {integ.comingSoon ? "Unavailable" : <>Configure Integration <ExternalLink size={16}/></>}
                  </button>
                </a>
              )}
            </div>
          ))}
        </div>
      )}

      {activeTab === "updates" && <OTAUpdatePanel />}

      {showTelegramModal && (
        <div style={{ position: "fixed", top: 0, left: 0, right: 0, bottom: 0, backgroundColor: "rgba(11, 20, 55, 0.5)", backdropFilter: "blur(4px)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 9999, padding: "20px" }}>
          <div style={{ maxWidth: "600px", width: "100%", animation: "fadeIn 0.2s ease-out" }}>
            <TelegramSetup onClose={() => setShowTelegramModal(false)} onUpdateSuccess={fetchMyProfile} />
          </div>
        </div>
      )}
      {showErpModal && (
        <div style={{ position: "fixed", top: 0, left: 0, right: 0, bottom: 0, backgroundColor: "rgba(11, 20, 55, 0.5)", backdropFilter: "blur(4px)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 9999, padding: "20px" }}>
          <div style={{ maxWidth: "600px", width: "100%", animation: "fadeIn 0.2s ease-out" }}>
            <ERPSetup onClose={() => setShowErpModal(false)} />
          </div>
        </div>
      )}
      {showHrmsModal && (
        <div style={{ position: "fixed", top: 0, left: 0, right: 0, bottom: 0, backgroundColor: "rgba(11, 20, 55, 0.5)", backdropFilter: "blur(4px)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 9999, padding: "20px" }}>
          <div style={{ maxWidth: "550px", width: "100%", animation: "fadeIn 0.2s ease-out" }}>
            <HRMSSetup onClose={() => setShowHrmsModal(false)} />
          </div>
        </div>
      )}

      {activeTab === "network" && (
        <div style={{ backgroundColor: "#FFFFFF", padding: "30px", borderRadius: "20px", boxShadow: "0px 4px 20px rgba(0,0,0,0.04)", maxWidth: "600px" }}>
          <h3 style={{ margin: "0 0 5px 0", color: "#2B3674", fontSize: "20px", fontWeight: "700" }}>Wi-Fi Configuration</h3>
          <p style={{ margin: "0 0 25px 0", color: "#A3AED0", fontSize: "14px" }}>Scan and connect the device to local wireless networks.</p>
          
          <WifiSetupPanel />
        </div>
      )}

    </div>
  );
}

const TabButton = ({ active, onClick, children }) => (
  <button onClick={onClick} style={{ padding: "10px 20px", backgroundColor: active ? "#4318FF" : "transparent", color: active ? "white" : "#A3AED0", border: "none", borderRadius: "12px", fontSize: "14px", fontWeight: "600", cursor: "pointer", transition: "all 0.3s ease" }}>{children}</button>
);

const ActionCard = ({ title, desc, icon, bgColor }) => (
  <div style={{ backgroundColor: "#FFFFFF", padding: "24px", borderRadius: "20px", boxShadow: "0px 4px 20px rgba(0,0,0,0.03)", display: "flex", alignItems: "center", gap: "20px", cursor: "pointer", transition: "transform 0.2s ease" }} onMouseOver={(e) => e.currentTarget.style.transform = "translateY(-4px)"} onMouseOut={(e) => e.currentTarget.style.transform = "translateY(0)"}>
    <div style={{ minWidth: "64px", height: "64px", borderRadius: "16px", backgroundColor: bgColor, display: "flex", alignItems: "center", justifyContent: "center" }}>{icon}</div>
    <div><h4 style={{ margin: "0 0 5px 0", color: "#2B3674", fontSize: "18px", fontWeight: "700" }}>{title}</h4><p style={{ margin: 0, color: "#A3AED0", fontSize: "13px", lineHeight: "1.4" }}>{desc}</p></div>
  </div>
);

const DetailItem = ({ label, value }) => (
  <div style={{ backgroundColor: "#F4F7FE", padding: "16px", borderRadius: "12px" }}>
    <p style={{ margin: "0 0 4px 0", color: "#A3AED0", fontSize: "12px", fontWeight: "600", textTransform: "uppercase" }}>{label}</p>
    <p style={{ margin: "0", color: "#2B3674", fontSize: "15px", fontWeight: "600", wordBreak: "break-all" }}>{value}</p>
  </div>
);

const ToggleCard = ({ title, desc, icon, bgColor, checked, onChange }) => (
  <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "20px", gap: "20px" }}>
    <div style={{ display: "flex", alignItems: "center", gap: "15px" }}>
      <div style={{ minWidth: "50px", height: "50px", borderRadius: "12px", backgroundColor: bgColor, display: "flex", alignItems: "center", justifyContent: "center" }}>{icon}</div>
      <div><h4 style={{ margin: "0 0 4px 0", color: "#2B3674", fontSize: "16px", fontWeight: "700" }}>{title}</h4><p style={{ margin: 0, color: "#A3AED0", fontSize: "13px", lineHeight: "1.4" }}>{desc}</p></div>
    </div>
    <label style={{ display: "flex", alignItems: "center", cursor: "pointer", position: "relative" }}>
      <input type="checkbox" style={{ opacity: 0, position: "absolute", width: 0, height: 0 }} checked={checked} onChange={onChange} />
      <div style={{ width: "48px", height: "26px", backgroundColor: checked ? "#05CD99" : "#E2E8F0", borderRadius: "20px", position: "relative", transition: "0.3s", display: "flex", alignItems: "center" }}>
        <div style={{ width: "20px", height: "20px", backgroundColor: "white", borderRadius: "50%", position: "absolute", left: checked ? "25px" : "3px", transition: "0.3s", boxShadow: "0 2px 4px rgba(0,0,0,0.2)" }}></div>
      </div>
    </label>
  </div>
);

const WifiSetupPanel = () => {
  const [networks, setNetworks] = useState([]);
  const [scanning, setScanning] = useState(false);
  const [selectedSsid, setSelectedSsid] = useState("");
  const [password, setPassword] = useState("");
  const [connecting, setConnecting] = useState(false);

  const scanWifi = async () => {
    setScanning(true);
    try {
      const { data } = await axios.get(`${REACT_APP_API_URL}/system/wifi/scan`);
      setNetworks(data.networks);
    } catch (error) {
      toast.error("Failed to scan for networks.");
    } finally {
      setScanning(false);
    }
  };

  const connectWifi = async () => {
    if (!selectedSsid || !password) return toast.warn("Select a network and enter a password");
    setConnecting(true);
    try {
      await axios.post(`${REACT_APP_API_URL}/system/wifi/connect`, { ssid: selectedSsid, password });
      toast.success(`Successfully connected to ${selectedSsid}`);
      setSelectedSsid("");
      setPassword("");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Connection failed.");
    } finally {
      setConnecting(false);
    }
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "15px" }}>
      <button 
        onClick={scanWifi} 
        disabled={scanning} 
        style={{ padding: "12px", backgroundColor: "#05CD99", color: "white", border: "none", borderRadius: "12px", fontWeight: "600", cursor: scanning ? "not-allowed" : "pointer", transition: "background 0.2s" }}
      >
        {scanning ? "Scanning..." : "Scan for Wi-Fi Networks"}
      </button>
      
      {networks.length > 0 && (
        <select 
          value={selectedSsid} 
          onChange={(e) => setSelectedSsid(e.target.value)} 
          style={{ padding: "12px", borderRadius: "12px", border: "1px solid #E2E8F0", outline: "none", color: "#2B3674", fontWeight: "500" }}
        >
          <option value="">Select a network...</option>
          {networks.map((nw, idx) => (
            <option key={idx} value={nw.ssid}>
              {nw.ssid} (Signal: {nw.signal} | Sec: {nw.security})
            </option>
          ))}
        </select>
      )}

      {selectedSsid && (
        <div style={{ display: "flex", flexDirection: "column", gap: "10px", marginTop: "10px" }}>
          <input 
            type="password" 
            placeholder={`Password for ${selectedSsid}`} 
            value={password} 
            onChange={(e) => setPassword(e.target.value)} 
            style={{ padding: "12px", borderRadius: "12px", border: "1px solid #E2E8F0", outline: "none" }} 
          />
          <button 
            onClick={connectWifi} 
            disabled={connecting} 
            style={{ padding: "12px", backgroundColor: "#4318FF", color: "white", border: "none", borderRadius: "12px", fontWeight: "600", cursor: connecting ? "not-allowed" : "pointer" }}
          >
            {connecting ? "Authenticating..." : "Connect"}
          </button>
        </div>
      )}
    </div>
  );
};
